import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

// Platform pricing rules — must mirror backend server.py constants
const FREE_DELIVERY_THRESHOLD = 20.00;
const DELIVERY_FEE_LOW = 2.99;
const DELIVERY_FEE_HIGH = 4.99;
export const computeDeliveryFee = (subtotal) =>
  subtotal >= FREE_DELIVERY_THRESHOLD ? DELIVERY_FEE_LOW : DELIVERY_FEE_HIGH;
export const DELIVERY_RULES = { FREE_DELIVERY_THRESHOLD, DELIVERY_FEE_LOW, DELIVERY_FEE_HIGH };

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("ce_cart");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [restaurant, setRestaurant] = useState(() => {
    try {
      const raw = localStorage.getItem("ce_cart_restaurant");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("ce_cart", JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    localStorage.setItem("ce_cart_restaurant", JSON.stringify(restaurant));
  }, [restaurant]);

  const addItem = useCallback((rest, item, qty = 1, addons = [], removed = []) => {
    setRestaurant((prev) => {
      if (prev && prev.id !== rest.id) {
        // switching restaurant - clear cart
        setItems([]);
      }
      return { id: rest.id, name: rest.name, delivery_fee: rest.delivery_fee, min_order: rest.min_order };
    });
    setItems((prev) => {
      const addonKey = (addons || []).map(a => a.name).sort().join(",");
      const removedKey = (removed || []).slice().sort().join(",");
      const key = `${item.id}:${addonKey}:no-${removedKey}`;
      const found = prev.find((p) => p.key === key);
      if (found) {
        return prev.map((p) => p.key === key ? { ...p, qty: p.qty + qty } : p);
      }
      const addonsCost = (addons || []).reduce((s, a) => s + (a.price || 0), 0);
      return [...prev, {
        key,
        item_id: item.id,
        name: item.name,
        price: item.price + addonsCost,
        base_price: item.price,
        image: item.image,
        addons: addons || [],
        removed_ingredients: removed || [],
        qty,
      }];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) => prev.map((p) => p.key === key ? { ...p, qty: Math.max(1, qty) } : p));
  }, []);
  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((p) => p.key !== key));
  }, []);
  const clear = useCallback(() => {
    setItems([]);
    setRestaurant(null);
  }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const deliveryFee = useMemo(() => (items.length === 0 ? 0 : computeDeliveryFee(subtotal)), [items.length, subtotal]);
  const amountToFreeTier = useMemo(
    () => Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal),
    [subtotal]
  );

  return (
    <CartContext.Provider value={{
      items, restaurant, addItem, updateQty, removeItem, clear,
      subtotal, count, deliveryFee, amountToFreeTier, drawerOpen, setDrawerOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
